/**
 * SpacesPage — Live Audio Cooking Rooms
 * Route: /spaces
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Mic, Users, Radio, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpacesPage = () => {
  const { user } = useAuth();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [categories, setCategories] = useState([]);
  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${API_URL}/api/spaces`).then(r => r.json())
      .then(d => { setSpaces(d.spaces || []); setCategories(d.categories || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!title.trim() || !user) return;
    const res = await fetch(`${API_URL}/api/spaces`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
      body: JSON.stringify({ title, category }),
    });
    if (res.ok) {
      setShowCreate(false); setTitle('');
      fetch(`${API_URL}/api/spaces`).then(r => r.json()).then(d => setSpaces(d.spaces || []));
    }
  };

  const handleJoin = async (id) => {
    if (!user) return;
    await fetch(`${API_URL}/api/spaces/${id}/join`, { method: 'POST', credentials: 'include', headers });
    fetch(`${API_URL}/api/spaces`).then(r => r.json()).then(d => setSpaces(d.spaces || []));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      <div className="relative" style={{ minHeight: '180px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-500 to-pink-500" />
        <div className="relative flex flex-col justify-end max-w-4xl mx-auto px-4" style={{ minHeight: '180px' }}>
          <div className="mb-5">
            <Link to="/" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs mb-2"><ArrowLeft className="w-3 h-3" /> Back</Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Radio className="w-8 h-8" /> Live Spaces</h1>
            <p className="text-white/70 text-sm mt-1">Join live cooking & food conversations</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm text-gray-500">{spaces.length} live now</span>
          </div>
          {user && <button data-testid="start-space-btn" onClick={() => setShowCreate(!showCreate)} className="h-9 px-4 bg-gray-900 text-white rounded-2xl text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" />Start Space</button>}
        </div>

        {showCreate && (
          <div data-testid="space-create-form" className="bg-white rounded-[20px] p-5 mb-5 shadow-sm">
            <input data-testid="space-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Space title..." className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-2 focus:outline-none" />
            <select data-testid="space-category-select" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-3">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button data-testid="go-live-btn" onClick={handleCreate} className="w-full py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold text-sm rounded-2xl">Go Live</button>
          </div>
        )}

        <div className="space-y-4">
          {loading ? [1,2].map(i => <div key={i} className="h-32 bg-gray-100 rounded-[20px] animate-pulse" />) :
          spaces.length === 0 ? (
            <div className="text-center py-16"><Radio className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-bold text-gray-700">No live spaces</h3><p className="text-sm text-gray-500">Start one — cook, chat, connect!</p></div>
          ) :
          spaces.map(sp => (
            <div key={sp.space_id} className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-[10px] text-red-500 font-bold uppercase">Live</span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{sp.category}</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">{sp.title}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    <Avatar className="w-5 h-5"><AvatarImage src={sp.host_avatar} /><AvatarFallback className="text-[8px] bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white">{(sp.host_name||'H')[0]}</AvatarFallback></Avatar>
                    <span className="text-xs text-gray-500">{sp.host_name}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1"><Users className="w-3 h-3" />{sp.speaker_count + sp.listener_count}</span>
                  </div>
                  <button data-testid={`join-space-${sp.space_id}`} onClick={() => handleJoin(sp.space_id)} className="px-4 py-1.5 bg-indigo-500 text-white text-xs font-semibold rounded-full hover:bg-indigo-600 transition-colors">Join</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpacesPage;
